import { useState, useCallback } from 'react';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import { createCategory, updateCategory, deleteCategory } from '../../api';
import type { PromptTemplateCategory, PromptTemplateCaseListing } from '../../api';
import { CategoryFormModal, CategoryManageModal, DeleteConfirmDialog } from '../index';
import styles from './CategoryFilter.module.scss';

interface CategoryFilterProps {
  categories: PromptTemplateCategory[];
  categoriesLoading?: boolean;
  selectedCategory: PromptTemplateCategory | null;
  onSelect: (category: PromptTemplateCategory | null) => void;
  onCategoriesChange?: () => void;
  isAdmin?: boolean;
  cases?: PromptTemplateCaseListing[];
  casesLoading?: boolean;
  selectedCaseId?: string | null;
  onCaseSelect?: (caseId: string | null) => void;
}

export default function CategoryFilter({
  categories,
  categoriesLoading = false,
  selectedCategory,
  onSelect,
  onCategoriesChange,
  isAdmin = false,
  cases = [],
  casesLoading = false,
  selectedCaseId = null,
  onCaseSelect,
}: CategoryFilterProps) {
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<PromptTemplateCategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PromptTemplateCategory | null>(null);
  const [manageModalOpen, setManageModalOpen] = useState(false);

  const handleCategorySubmit = useCallback(
    async (form: { title: string; color: string; description?: string }) => {
      if (editingCategory) {
        await updateCategory(editingCategory.id, form);
      } else {
        await createCategory(form);
      }
      onCategoriesChange?.();
      setCategoryModalOpen(false);
      setEditingCategory(null);
    },
    [editingCategory, onCategoriesChange]
  );

  const handleDeleteCategory = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteCategory(deleteTarget.id);
    if (selectedCategory?.id === deleteTarget.id) {
      onSelect(null);
    }
    onCategoriesChange?.();
    setDeleteTarget(null);
  }, [deleteTarget, selectedCategory, onSelect, onCategoriesChange]);

  const handleAddCategory = useCallback(() => {
    setEditingCategory(null);
    setCategoryModalOpen(true);
  }, []);

  const handleEditCategory = useCallback((cat: PromptTemplateCategory) => {
    setManageModalOpen(false);
    setEditingCategory(cat);
    setCategoryModalOpen(true);
  }, []);

  const handleDeleteCategoryClick = useCallback((cat: PromptTemplateCategory) => {
    setManageModalOpen(false);
    setDeleteTarget(cat);
  }, []);

  if (categoriesLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.chips}>Загрузка категорий…</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.headerRow}>
        <span className={styles.sectionLabel}>Категории</span>
        {isAdmin && (
          <button
            type="button"
            className={styles.manageButton}
            onClick={() => setManageModalOpen(true)}
            title="Управление категориями"
          >
            <SettingsIcon sx={{ fontSize: 16 }} />
          </button>
        )}
      </div>
      <div className={styles.chips}>
        <button
          type="button"
          className={`${styles.chip} ${!selectedCategory ? styles.chipSelected : ''}`}
          onClick={() => onSelect(null)}
        >
          Все
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            className={`${styles.chip} ${selectedCategory?.id === cat.id ? styles.chipSelected : ''}`}
            style={
              selectedCategory?.id === cat.id
                ? { backgroundColor: cat.color, borderColor: cat.color, color: '#fff' }
                : { borderColor: cat.color, color: cat.color }
            }
            onClick={() => onSelect(selectedCategory?.id === cat.id ? null : cat)}
            title={cat.description ?? cat.title}
          >
            {cat.title}
          </button>
        ))}
        {isAdmin && (
          <button type="button" className={styles.chipAdd} onClick={handleAddCategory}>
            <AddIcon sx={{ fontSize: 20 }} />
            Добавить категорию
          </button>
        )}
      </div>
      {onCaseSelect && (
        <div className={styles.caseFilterRow}>
          <span className={styles.caseFilterLabel}>По кейсу:</span>
          <div className={styles.chips}>
            <button
              type="button"
              className={`${styles.chip} ${!selectedCaseId ? styles.chipSelected : ''}`}
              onClick={() => onCaseSelect(null)}
              disabled={casesLoading}
            >
              Все
            </button>
            {cases.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`${styles.chip} ${selectedCaseId === c.id ? styles.chipSelected : ''}`}
                onClick={() => onCaseSelect(selectedCaseId === c.id ? null : c.id)}
              >
                {c.title}
              </button>
            ))}
          </div>
        </div>
      )}

      <CategoryFormModal
        open={categoryModalOpen}
        onClose={() => {
          setCategoryModalOpen(false);
          setEditingCategory(null);
        }}
        onSubmit={handleCategorySubmit}
        category={editingCategory}
      />

      <CategoryManageModal
        open={manageModalOpen}
        onClose={() => setManageModalOpen(false)}
        categories={categories}
        onEdit={handleEditCategory}
        onDelete={handleDeleteCategoryClick}
      />

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteCategory}
        title="Удалить категорию?"
        message={
          deleteTarget
            ? `Категория «${deleteTarget.title}» будет удалена. Шаблоны не удаляются.`
            : ''
        }
      />
    </div>
  );
}
